/**
 * What the incoming-call card shows, derived only from data that already
 * exists: /api/caller-insights (lead, caller ID, spam risk, relationship
 * history) and the org's numbers (friendly name of the line that was called).
 */

export interface IncomingCardData {
  name: string;
  number: string;
  verified: boolean;
  avatarUrl: string | null;
  /** Up to three short context rows. */
  rows: { kind: 'tag' | 'company' | 'last'; text: string }[];
  spam: { level: 'medium' | 'high'; reason: string | null } | null;
}

function ago(iso: string, now: number): string | null {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  const mins = Math.max(0, Math.round((now - t) / 60000));
  if (mins < 60) return `${mins || 1} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.round(hrs / 24);
  return days === 1 ? 'yesterday' : `${days} days ago`;
}

export function buildIncomingCard(
  number: string,
  insights: any | null | undefined,
  fallback: { name?: string; company?: string } = {},
  now = Date.now(),
): IncomingCardData {
  const lead = insights?.lead ?? null;
  const name = lead?.fullName || insights?.callerIdInfo?.callerName || fallback.name || '';
  const rows: IncomingCardData['rows'] = [];
  const tag = Array.isArray(lead?.tags) && lead.tags.find((t: unknown) => typeof t === 'string' && t.trim());
  if (tag) rows.push({ kind: 'tag', text: String(tag) });
  const company = lead?.company || fallback.company;
  if (company) rows.push({ kind: 'company', text: String(company) });
  const recent = insights?.relationshipHistory?.recentActivity?.[0];
  const lastIso = recent?.timestamp || insights?.relationshipHistory?.lastContactDate;
  const when = lastIso ? ago(String(lastIso), now) : null;
  if (when) {
    const what = recent?.type === 'message' ? 'Last message' : 'Last call';
    rows.push({ kind: 'last', text: `${what} ${when}` });
  }
  const risk = insights?.spamRisk;
  const spam = risk && (risk.isSpam || risk.riskLevel === 'high')
    ? { level: (risk.riskLevel === 'high' || risk.isSpam ? 'high' : 'medium') as 'high' | 'medium', reason: risk.reason || null }
    : null;
  return {
    name,
    number,
    verified: !!(insights?.isKnown && lead),
    avatarUrl: insights?.socialProfile?.profileImageUrl || null,
    rows: rows.slice(0, 3),
    spam,
  };
}

const digits = (s: unknown) => String(s || '').replace(/\D/g, '');

/** The called line's friendly name, else the number itself (null when unknown). */
export function lineLabel(calledNumber: string | null | undefined, numbers: Array<{ phoneNumber?: string; friendlyName?: string | null }> | null | undefined): string | null {
  if (!calledNumber) return null;
  const d = digits(calledNumber);
  const hit = (numbers || []).find((n) => d && digits(n.phoneNumber) === d);
  const fn = hit?.friendlyName?.trim();
  return fn && digits(fn) !== d ? fn : null;
}

export function initialsOf(name: string): string {
  return name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}
